var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as yaml from 'js-yaml';
import { constructAction, validateExpressionComponents } from './actions';
import { convertValueToExpression, findArgument, getArgumentErrorAggregate, getClosestRelatedActionArgumentHandle, } from './arguments';
import { getDefaultBlueprintRegistryClient, } from './blueprintRegistryClient';
import { constructFromDefinition, deconstructOutput } from './definitions';
import { DominoBuilderError, DominoBuilderErrorScope, DominoBuilderErrorSeverity, DominoBuilderErrorType, filterErrors, } from './errors';
import { getArrayType, getRootHandle } from './expressions/helpers';
import { generateResultUniqueName, getIsAnyType, performInitialArgumentValueValidation, validateInputs, } from './helpers';
import { constructParameters } from './parameters';
import { resourceRegistryWithCache, } from './resourceRegistry';
import { searchSymbols } from './search';
import { constructContextSymbol, constructTypeIndex, FlowSymbolTable, } from './symbols';
import { constructTriggerCall } from './triggers';
import { DefinitionType, DefinitionValidity, HandleType, } from './types';
export default class DominoBuilder {
    constructor(resourceRegistryClient, blueprintRegistryClient) {
        this.errors = [];
        this.updateSymbolReferences = () => __awaiter(this, void 0, void 0, function* () {
            const output = this.output;
            output.actions.forEach((a, idx) => __awaiter(this, void 0, void 0, function* () {
                const scopedSymbol = this.symbolTable.getScopedSymbol(a.result, a.scopeRef);
                if (!!scopedSymbol) {
                    scopedSymbol.lineNumber = idx;
                    this.symbolTable.setScopedSymbol(a.result, scopedSymbol, a.scopeRef);
                    yield constructTypeIndex(scopedSymbol, this.symbolTable, this.resourceRegistry, a.scopeRef);
                }
            }));
        });
        this.resourceRegistry = resourceRegistryWithCache(resourceRegistryClient);
        this.blueprintRegistryClient =
            blueprintRegistryClient !== null && blueprintRegistryClient !== void 0 ? blueprintRegistryClient : getDefaultBlueprintRegistryClient();
        this.symbolTable = new FlowSymbolTable();
    }
    load(definition) {
        return __awaiter(this, void 0, void 0, function* () {
            let parsedDefinition;
            try {
                parsedDefinition = yaml.load(definition);
            }
            catch (error) {
                if (error instanceof yaml.YAMLException) {
                    this.errors.push(new DominoBuilderError(DominoBuilderErrorType.ParseError, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.FATAL, 'The flow could not be parsed', {
                        path: `Global`,
                    }));
                    return this;
                }
            }
            return yield this.loadInternal(parsedDefinition);
        });
    }
    initializeWithType(type, name) {
        return __awaiter(this, void 0, void 0, function* () {
            let flow;
            name = name !== null && name !== void 0 ? name : 'Untitled';
            if (type === DefinitionType.Action) {
                flow = {
                    action: {
                        name,
                        spec: [],
                    },
                };
            }
            else if (type === DefinitionType.Blueprint) {
                flow = {
                    blueprint: {
                        name,
                        spec: {
                            trigger: '',
                            actions: [],
                        },
                    },
                };
            }
            else {
                flow = {
                    flow: {
                        name,
                        spec: {
                            trigger: '',
                            actions: [],
                        },
                    },
                };
            }
            return yield this.loadInternal(flow);
        });
    }
    initializeFlowWithBlueprint(blueprintId, name) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.loadInternal({
                flow: {
                    name: name !== null && name !== void 0 ? name : 'Untitled',
                    spec: {
                        blueprintRef: blueprintId,
                        args: [],
                    },
                },
            });
        });
    }
    loadInternal(def) {
        return __awaiter(this, void 0, void 0, function* () {
            this.output = yield constructFromDefinition(def, this.blueprintRegistryClient, this.resourceRegistry, this.symbolTable);
            return this;
        });
    }
    getAggregatedErrors(scope, severity) {
        var _a, _b, _c, _d;
        return filterErrors(scope, severity, (_a = this.errors) !== null && _a !== void 0 ? _a : []).concat((_d = (_c = (_b = this.output) === null || _b === void 0 ? void 0 : _b.getErrorAggregate) === null || _c === void 0 ? void 0 : _c.call(_b, scope, severity)) !== null && _d !== void 0 ? _d : []);
    }
    validateDefinition() {
        var _a, _b;
        const errors = this.getAggregatedErrors(DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR);
        if (!!errors.find(x => x.severity === DominoBuilderErrorSeverity.FATAL)) {
            return { validity: DefinitionValidity.FATAL, errors: errors };
        }
        if (!!errors.find(x => x.severity === DominoBuilderErrorSeverity.ERROR)) {
            return { validity: DefinitionValidity.INVALID, errors };
        }
        if (!(((_b = (_a = this.output) === null || _a === void 0 ? void 0 : _a.actions) === null || _b === void 0 ? void 0 : _b.length) > 0)) {
            return { validity: DefinitionValidity.EMPTY };
        }
        return { validity: DefinitionValidity.VALID, errors: [] };
    }
    addParameter(type) {
        var _a;
        const output = this.output;
        if (!output.parameters) {
            output.parameters = [];
        }
        const count = (_a = output.parameters.filter(p => p.name.startsWith('UntitledParam_')).length +
            1) !== null && _a !== void 0 ? _a : 0;
        output.parameters.concat(constructParameters([
            {
                name: 'UntitledParam_' + count,
                type,
            },
        ]));
        return this;
    }
    /**
     * Adds an action of the given type to the end of the flow (looks up action by type within the provided action registry)
     * @param type
     * @returns
     */
    addAction(type) {
        return __awaiter(this, void 0, void 0, function* () {
            const output = this.output;
            if (!output.actions) {
                output.actions = [];
            }
            const callDefinition = {
                call: type,
                result: generateResultUniqueName(type),
                args: {},
            };
            const action = yield constructAction(output.actions.length, callDefinition, this.resourceRegistry, this.symbolTable);
            output.actions.push(action);
            return [this, action.handle];
        });
    }
    deleteAction(handle) {
        return __awaiter(this, void 0, void 0, function* () {
            const output = this.output;
            const action = output.actions.find(a => a.handle.id === handle.id);
            if (action) {
                output.actions = output.actions.filter(a => a.handle.id !== handle.id);
                this.symbolTable.deleteScopedSymbol(action.result, action.scopeRef);
                // update line numbers
                output.actions = output.actions.map((a, idx) => {
                    return Object.assign(Object.assign({}, a), { lineNumber: idx });
                });
                yield this.updateSymbolReferences();
            }
            return this;
        });
    }
    setFlowName(value) {
        this.output.name = value;
        return this;
    }
    setDescription(value) {
        this.output.description = value;
        return this;
    }
    setActionLabel(handle, value) {
        const output = this.output;
        output.actions.find(a => a.handle.id === handle.id).label = value;
        return this;
    }
    setActionType(handle, type) {
        return __awaiter(this, void 0, void 0, function* () {
            const output = this.output;
            const actionIndex = output.actions.findIndex(a => a.handle.id === handle.id);
            this.symbolTable.deleteScopedSymbol(output.actions[actionIndex].result, output.actions[actionIndex].scopeRef);
            const callDefinition = {
                call: type,
                result: generateResultUniqueName(type),
                args: {},
            };
            const newAction = yield constructAction(actionIndex, callDefinition, this.resourceRegistry, this.symbolTable, output.actions[actionIndex].handle);
            output.actions[actionIndex] = newAction;
            return this;
        });
    }
    addActionAfter(type, afterHandle) {
        return __awaiter(this, void 0, void 0, function* () {
            const output = this.output;
            const callDefinition = {
                call: type,
                result: generateResultUniqueName(type),
                args: {},
            };
            const targetIndex = output.actions.findIndex(a => a.handle.id === afterHandle.id) + 1;
            const newAction = yield constructAction(targetIndex, callDefinition, this.resourceRegistry, this.symbolTable);
            output.actions.splice(targetIndex, 0, newAction);
            // update line numbers
            output.actions = output.actions.map((a, idx) => {
                return Object.assign(Object.assign({}, a), { lineNumber: idx });
            });
            yield this.updateSymbolReferences();
            return [this, newAction.handle];
        });
    }
    setTriggerType(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.output.trigger = yield constructTriggerCall({ name }, this.resourceRegistry, this.symbolTable);
            return this;
        });
    }
    setArgument(handle, value) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let output;
            let target;
            let rootHandle;
            const argByHandle = (a) => a.handle.id === handle.id;
            const setTargetValue = (target, value, action) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c;
                if (!target) {
                    return;
                }
                const scopeRef = (_b = action === null || action === void 0 ? void 0 : action.scopeRef) !== null && _b !== void 0 ? _b : 'global';
                const lineNumber = (_c = action === null || action === void 0 ? void 0 : action.lineNumber) !== null && _c !== void 0 ? _c : -1;
                const effectiveScope = this.symbolTable.calculateEffectiveScope(scopeRef);
                let { errors, validatedValue } = performInitialArgumentValueValidation(handle, value, target);
                if (handle.type === HandleType.TriggerGenericTypeArgument) {
                    //trigger input validation
                    validatedValue = validateInputs(validatedValue);
                }
                target.errors = errors;
                target.value = yield convertValueToExpression(handle, this.resourceRegistry, validatedValue, target.name, target.type, errors);
                validateExpressionComponents(target.type, target.value, effectiveScope, lineNumber);
                target.getErrorAggregate = (scope, severity) => getArgumentErrorAggregate(target.value, errors, scope, severity);
            });
            switch (handle.type) {
                case HandleType.ActionArgument:
                    output = this.output;
                    rootHandle = getRootHandle(handle);
                    const action = output.actions.find(a => a.handle.id === rootHandle.id);
                    target = findArgument(handle, action.argsCollection);
                    yield setTargetValue(target, value, action);
                    break;
                case HandleType.BlueprintArgument:
                    output = this.output;
                    target = output.blueprintReference.argsCollection.args.find(argByHandle);
                    yield setTargetValue(target, value);
                    break;
                case HandleType.TriggerArgument:
                    output = this.output;
                    target = output.trigger.argsCollection.args.find(argByHandle);
                    yield setTargetValue(target, value);
                    break;
                case HandleType.TriggerContextArgument:
                    output = this.output;
                    rootHandle = getRootHandle(handle);
                    target = output.trigger.context.args.find(a => a.handle.id === rootHandle.id);
                    if (target.handle.id !== handle.id) {
                        target = findArgument(handle, target.subArgsCollection);
                    }
                    yield setTargetValue(target, value);
                    break;
                case HandleType.TriggerGenericTypeArgument:
                    output = this.output;
                    target = output.trigger.genericTypeArgsCollection.args.find(argByHandle);
                    yield setTargetValue(target, value);
                    if (target) {
                        // Cache the updated generic type definition in the entity registry
                        this.resourceRegistry.setResourceToCache(target.name, {
                            name: target.name,
                            properties: (_a = target.value) === null || _a === void 0 ? void 0 : _a.map(genericArg => { var _a; return (_a = genericArg === null || genericArg === void 0 ? void 0 : genericArg.members[0]) === null || _a === void 0 ? void 0 : _a.value; }),
                            spec: {},
                        });
                        // Reconstruct context in the symbol table since we updated a type
                        constructContextSymbol(output.trigger.context.args, this.resourceRegistry, this.symbolTable);
                        // need to re-evaluate all expressions which potentially reference this changing symbol
                        // ideally, in the future we could also auto-update the symbol references to avoid breakage on changes
                        output.actions.map(a => a.validateExpressions(a.scopeRef));
                    }
                    break;
            }
            return this;
        });
    }
    /**
     * Generates the trigger payload for triggering, based on values set on the {@link TriggerCall#context}
     * @returns {Record<string,any>}
     */
    getTriggerPayload() {
        const output = this.output;
        let triggerPayload = {};
        triggerPayload = output.trigger.context.args.reduce((triggerPayload, arg) => {
            return Object.assign(Object.assign({}, triggerPayload), { [arg.name]: arg.value });
        }, triggerPayload);
        return triggerPayload;
    }
    getDefinition() {
        const out = deconstructOutput(this.output);
        return yaml.dump(out, { lineWidth: -1 });
    }
    getTypeDefinition(fqn) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.resourceRegistry.getResource(fqn);
            }
            catch (e) {
                return null;
            }
        });
    }
    querySymbolsForArgument(handle, options = {}) {
        var _a, _b;
        const output = this.output;
        const rootHandle = getRootHandle(handle);
        const action = output.actions.find(a => a.handle.id === rootHandle.id);
        const closestArgument = getClosestRelatedActionArgumentHandle(handle);
        const argument = findArgument(closestArgument, action.argsCollection);
        const symbols = this.symbolTable
            .getSymbolsForSearchIndex(action.scopeRef)
            .filter(symbol => !!symbol.id);
        if (!!options.returnAllSymbols && !!!options.searchPattern) {
            return symbols.filter(sym => sym.lineNumber < action.lineNumber);
        }
        return searchSymbols(symbols, getIsAnyType(argument.type)
            ? handle.type === HandleType.ArrayItem
                ? getArrayType(argument.type)
                : argument.type
            : '', (_a = options.enforceStrictType) !== null && _a !== void 0 ? _a : false, action.lineNumber, (_b = options.searchPattern) !== null && _b !== void 0 ? _b : '');
    }
}
//# sourceMappingURL=DominoBuilder.js.map